import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import Roles from "../../../models/roles";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { resetCentersErrorAction } from "../../../store/centers/slice";
import {
  CenterUserInactiveThunk,
  createCenterUsersThunk,
  updateCenterUsersThunk,
} from "../../../store/centers/thunks";
import {
  TCenterDetails,
  TCreateCenterUserValues,
} from "../../../store/centers/types";
import {
  createCentreUserThunk,
  updateCentreUserThunk,
} from "../../../store/superadmin/thunks";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { capitalizeName } from "../../../utils/stringUtils";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Checkbox from "../../UI/Inputs/Checkbox";
import InputTags from "../../UI/Inputs/InputTags";
import RadioInput from "../../UI/Inputs/RadioInput";
import TextInput from "../../UI/Inputs/TextInput";
import PopupModal from "../../UI/Popup";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

interface PopupModalUserProps {
  editingUser: any;
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  setShowNotification: Dispatch<SetStateAction<boolean>>;
  user: string;
  editValues?: any;
  setEditingUser: (value: number) => void;
  setEditValues: (value: any) => void;
  refreshUserList?: () => void;
  fetchUsers?: () => void;
}

const Users: FC<PopupModalUserProps> = ({
  editingUser,
  showModal,
  setShowModal,
  setShowNotification,
  user,
  editValues,
  setEditingUser,
  setEditValues,
  refreshUserList,
  fetchUsers,
}) => {
  const dispatch = useDispatch();

  const inProgress = useSelector((state) => state.Centres.inProgress);
  const superAdminInProgress = useSelector((state) => state.superadmin.inProgress);
  const centreuserInactiveProgress = useSelector(
    (state) => state.Centres.centreuserInactiveProgress
  )
  const error = useSelector((state) => state.superadmin.error);
  const userData = useSelector((state) => state.auth.user);
  const centreUsererror = useSelector((state) => state.Centres.error);
  const allCentersList = useSelector((state) => state.Centres.allCenters);
  const getAllActiveCentres = useSelector((state) => state.superadmin.allActiveCentres);

  const [isHavingTimesheetAccess, setIsHavingTimesheetAccess] = useState<boolean>(false);
  const [centresList, setCentresList] = useState<Array<any>>([]);

  const toggleIsHavingTimesheetAccess = () => {
    setIsHavingTimesheetAccess(!isHavingTimesheetAccess);
  };

  const isAdmin = useMemo(
    () =>
      userData?.role.name === Roles.SUPER_ADMIN ||
      userData?.role.name === Roles.GENERAL_ADMIN,
    [userData]
  );

  const isSuperOrCentreAdmin = useMemo(
    () =>
      userData?.role.name === Roles.SUPER_ADMIN ||
      userData?.role.name === Roles.CENTER_ADMIN,
    [userData]
  );

  useEffect(() => {
    if (user === "admin") {
      const filteredCentres = getAllActiveCentres?.filter(
        (centre) => centre.business_name
      );

      if (filteredCentres) {
        setCentresList(filteredCentres);
      } else {
        setCentresList([]);
      }
    } else if (user === "client") {
      setCentresList(allCentersList);
    }
  }, [user, setCentresList, allCentersList, getAllActiveCentres]);

  /**
   * Handles the selection of a user type and updates the form field value for "role_id".
   *
   * @param value - The string value representing the selected user type.
   *               The value is converted to a number and set as the form field value.
   */
  const handleChooseUserType = (value: string) => {
    setFieldValue("role_id", parseInt(value));
    if(parseInt(value) === 3){
      setFieldValue("has_all_centre_access", 0);
    }
    if (parseInt(value) === 4) {
      setFieldValue("centre_ids", []);
    }
  };

  /**
   * Handles the change in selected tags and updates the form field value for "centre_ids".
   *
   * @param centreTagId - An array of id representing the selected tags.
   *               Each tag corresponds to the `id` of a center.
   *
   * The function maps the selected tags to their corresponding center IDs
   * by finding the matching center in the `allCentersList` based on the `id`.
   * It then updates the form field "centre_ids" with the array of selected center IDs.
   */
  const handleTagsChange = (tags: number | Array<number>) => {
    let selectedTagValue: number[] = [];
    if (Array.isArray(tags) && tags.length > 0) {
      selectedTagValue = tags
        .map((id) => Number(id))
        .filter((id) => !isNaN(id));
    }
    setFieldValue("centre_ids", selectedTagValue);
  };

  /**
   * Handles the successful saving of a user by displaying a notification.
   *
   * // Optional: Auto-hide after 3 seconds
   */
  const handleSaveSuccess = () => {
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
      refreshUserList && refreshUserList();
      if (fetchUsers) {
        fetchUsers();
      }
    }, 3000); // Optional: Auto-hide after 3 seconds
  };

  /**
   * Custom hook for managing form state and submission logic for creating or updating center users.
   *
   * This hook utilizes `useFormik` to handle form state, validation, and submission. It includes
   * initial values, validation schema, and submission logic for creating or updating center users.
   *
   */
  const { handleSubmit, values, errors, touched, setFieldValue, handleChange, setErrors } =
    useFormik({
      validationSchema: isAdmin ? createValidationSchema("adminCenterUsers") : createValidationSchema("centerUsers"),
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        first_name: "",
        last_name: "",
        email: "",
        role_id: 0,
        centre_ids: [],
        has_all_centre_access: 0,
        phone_number: "",
        timesheet_access: 0,
        id: 0,
      },
      onSubmit: async (values: TCreateCenterUserValues) => {
        const payload = {
          ...values,
          first_name: capitalizeName(values.first_name),
          last_name: capitalizeName(values.last_name),
        };
        try {
          let result;
          if (user === "client") {
            result = editingUser
              ? await dispatch(
                  updateCenterUsersThunk({ ...payload, id: editingUser })
                )
              : await dispatch(createCenterUsersThunk(payload));
          } else if (user === "admin") {
            result = editingUser
              ? await dispatch(
                  updateCentreUserThunk({ ...payload, user_id: editingUser })
                )
              : await dispatch(createCentreUserThunk(payload));
          }

          if (isFulfilled(result)) {
            setShowModal(false);
            handleSaveSuccess();
            setFieldValue("timesheet_access", 0);
            setIsHavingTimesheetAccess(false)
            setFieldValue("has_all_centre_access", 0);
          } else {
            setShowModal(true);
          }
        } catch (error) {
          throw new Error(
            "Could not connect to services at the moment. Please try again. Thanks."
          );
        }
      },
    });

  useEffect(() => {
    setFieldValue("timesheet_access", isHavingTimesheetAccess);
  }, [setFieldValue, isHavingTimesheetAccess]);

  useEffect(() => {
    if (editValues && editValues !== null) {
      let userSelectedTagList: Array<number> = [];
      editValues?.centres?.forEach((centre: TCenterDetails) => {
        if (!userSelectedTagList.includes(centre.id)) {
          userSelectedTagList.push(centre.id);
        }
      });

      setFieldValue("first_name", capitalizeName(editValues?.first_name));
      setFieldValue("last_name", capitalizeName(editValues?.last_name));
      setFieldValue("email", editValues?.email);
      setFieldValue("phone_number", editValues?.phone_number);
      setFieldValue("role_id", editValues?.role_id || 0); // Default to 0 if undefined
      setFieldValue("centre_ids", userSelectedTagList || []); // Default to empty array if undefined
      setFieldValue("timesheet_access", editValues?.timesheet_access || 0); // Default to 0 if undefined
      setFieldValue("has_all_centre_access", editValues?.has_all_centre_access || 0); // Default to 0 if undefined
    }
  }, [editValues, setFieldValue]);

  /**
   * Handles the closing of the modal by toggling the `showModal` state to `false`.
   */
  const onClickClose = () => {
    dispatch(resetCentersErrorAction())
    setShowModal(false);
    setEditingUser(0);
    setEditValues(null);
    setErrors({});// when closing the modal, reset the errors , added fix for ACC-180
  };

  useEffect(() => {
    if (editValues === null && !editValues) {
      setFieldValue("first_name", "");
      setFieldValue("last_name", "");
      setFieldValue("email", "");
      setFieldValue("phone_number", "");
      setFieldValue("role_id", 0); // Default to 0 if undefined
      setFieldValue("centre_ids", []); // Default to empty array if undefined
      setFieldValue("timesheet_access", 0); // Default to 0 if undefined
      setFieldValue("has_all_centre_access", 0); // Default to 0 if undefined
    }
  }, [showModal, setFieldValue, editValues]);

  const handleMakeInactive = async () => {
    await dispatch(CenterUserInactiveThunk({ user_id: editingUser }));
    setShowModal(false);
    if (fetchUsers) {
      fetchUsers();
    }
  };

  const handleAccessToAllCentresChange = (checked: boolean) => {
    setFieldValue("centre_ids", [])
    setFieldValue("has_all_centre_access", checked ? 1 : 0);
  };

  return (
    <>
      <PopupModal
        visible={showModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
      >
        <div className="d-flex flex-column gap-15">
          <div className="fs-22 fw-400 text-black">
            {!editingUser ? "Create a User" : "Edit a User"}
          </div>
          <div className="bg-off-white h-1px w-100"></div>
          <div className="fs-16 text-black">Select a user type:</div>
          <div className="d-flex flex-column gap-10">
            <div className="theme-check">
              <RadioInput
                checked={String(values.role_id) === "3"}
                label={"Centre Admin"}
                onChange={handleChooseUserType}
                value={"3"}
                name="role_id"
                themeCheck={true}
              />
            </div>
            <div className="theme-check">
              <RadioInput
                checked={String(values.role_id) === "4"}
                label={"Centre User"}
                onChange={handleChooseUserType}
                value={"4"}
                name="role_id"
                themeCheck={true}
              />
            </div>
            {errors && <ErrorMessage message={errors.role_id as string} />}
          </div>
          <div className="w-100 h-1px"></div>
          <div className="fs-16 text-black">Add user details:</div>
          <div className="d-flex flex-column flex-lg-row gap-10">
            <div className="w-100">
              <TextInput
                value={values.first_name || ""}
                field={"first_name"}
                label={"First Name"}
                onChange={handleChange}
                touched={touched.first_name! as boolean}
                error={errors.first_name as string}
                placeholder={" "}
              />
            </div>
            <div className="w-100">
              <TextInput
                value={values.last_name || ""}
                field={"last_name"}
                label={"Last Name"}
                onChange={handleChange}
                touched={touched.last_name! as boolean}
                error={errors.last_name as string}
                placeholder={" "}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row gap-10">
            <div className="w-100">
              <TextInput
                value={values.email || ""}
                field={"email"}
                label={"Email Address"}
                onChange={handleChange}
                touched={touched.email! as boolean}
                error={errors.email as string}
                placeholder={" "}
              />
              {(error || centreUsererror) && (
                <ErrorMessage
                  message={
                    error?.errors?.email?.[0] || centreUsererror?.errors?.email?.[0]
                  }
                />
              )}
            </div>
            <div className="w-100">
              <TextInput
                value={values.phone_number || ""}
                field={"phone_number"}
                label={"Work Mobile Number"}
                onChange={handleChange}
                touched={touched.phone_number! as boolean}
                error={errors.phone_number as string}
                placeholder={" "}
              />
            </div>
          </div>
          <div className="w-100 h-1px"></div>
          { (isAdmin && String(values.role_id) === "3") || String(values.role_id) === "4" ? 
            <>
              <div className="fs-16 fw-400 text-black">
                Assign user to centres:
              </div>
              <InputTags
                field={"centre_ids"}
                selectedValue={values.centre_ids}
                onTagsChange={handleTagsChange}
                allCenters={centresList}
                allcentreAccess={isAdmin ? 0 : values?.has_all_centre_access}
                // allcentreAccess={values?.has_all_centre_access}
              />
              {errors && <ErrorMessage message={errors.centre_ids as string} />}
            </>
          : "" }
          { !isAdmin && String(values.role_id) === "4" ? 
            <div className="theme-check">
              <Checkbox
                checked={values?.has_all_centre_access === 1}
                label="Access to all centres"
                id={"has_all_centre_access"}
                onChange={handleAccessToAllCentresChange}
                themeCheck={true}
              /> 
            </div> : ""
          }
          { values.role_id === 4 ? 
            <>
              <div className="w-100 h-1px"></div>
              <div className="fs-16 fw-400 text-black">Set permission levels:</div>
              <div className="theme-check">
                <Checkbox
                  checked={values?.timesheet_access === 1 ? values?.timesheet_access === 1 : isHavingTimesheetAccess}
                  themeCheck={true}
                  label="Allow access to Timesheet"
                  onChange={toggleIsHavingTimesheetAccess}
                  id={"allowTimesheet"}
                />
                {errors && (
                  <ErrorMessage message={errors.timesheet_access as string} />
                )}
              </div>
            </> : ""
          }
          <div className="w-100 h-1px"></div>
          <div className="d-flex flex-column flex-lg-row gap-10 justify-content-lg-between">
            {isSuperOrCentreAdmin && values.role_id === 4 && editingUser ? (
              <CustomButton
                variant={"primary"}
                className="btn-theme text-black d-flex"
                onClick={handleMakeInactive}
                contentClass="d-flex"
                isLoading={centreuserInactiveProgress}
                type="submit"
              >
                <RoundedTick
                  color={
                    centreuserInactiveProgress ? "#aaaaaa" : "#000000"
                  }
                />
                Make Inactive
              </CustomButton>
            ) : (
              <div></div>
            )}
            <CustomButton
              variant={"primary"}
              className="btn-theme bg-teal text-black d-flex"
              onClick={handleSubmit}
              contentClass="d-flex"
              isLoading={inProgress || superAdminInProgress}
              type="submit"
            >
              <RoundedTick
                color={
                  inProgress || superAdminInProgress ? "#aaaaaa" : "#000000"
                }
              />
              {!editingUser ? "Save and Invite" : "Update User"}
            </CustomButton>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default Users;
